import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
	Box,
	Card,
	CardContent,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import { FC, MouseEvent, useMemo, useState } from 'react';
import { COLORS } from 'services/constants/colors';
import { UserAvatar } from 'ui/UserAvatar';
import { KANBAN_COLUMNS, SYNTHETIC_TASKS } from './constants';
import { TTaskItem, TTaskTrackerProps, TTaskTrackerView } from './types';

const TaskCard: FC<{ task: TTaskItem }> = ({ task }) => (
	<Card variant="outlined" sx={{ borderRadius: 2 }}>
		<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
			<Typography variant="subtitle2" fontWeight={700} gutterBottom>
				{task.title}
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				{task.description}
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
				<UserAvatar user={task.assignee} size="xs" />
			</Box>
		</CardContent>
	</Card>
);

export const TaskTracker: FC<TTaskTrackerProps> = () => {
	const [view, setView] = useState<TTaskTrackerView>('kanban');

	const tasksByStatus = useMemo(() => {
		const map: Record<string, TTaskItem[]> = {
			todo: [],
			in_progress: [],
			in_review: [],
			done: [],
		};
		SYNTHETIC_TASKS.forEach((task) => {
			map[task.status].push(task);
		});
		return map;
	}, []);

	const handleViewChange = (_: MouseEvent<HTMLElement>, next: TTaskTrackerView | null) => {
		if (next !== null) setView(next);
	};

	return (
		<Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
				<ToggleButtonGroup
					color="primary"
					size="small"
					value={view}
					exclusive
					onChange={handleViewChange}
					aria-label="Task view"
				>
					<ToggleButton value="kanban" aria-label="Kanban view">
						<ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} />
						Kanban
					</ToggleButton>
					<ToggleButton value="list" aria-label="List view">
						<ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
						List
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{view === 'kanban' ? (
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: {
							xs: '1fr',
							md: 'repeat(2, minmax(0, 1fr))',
							lg: 'repeat(4, minmax(0, 1fr))',
						},
						gridAutoRows: '1fr',
						gap: 2,
						alignItems: 'stretch',
						minHeight: 'calc(100dvh - 200px)',
					}}
				>
					{KANBAN_COLUMNS.map((col) => (
						<Paper
							key={col.status}
							elevation={0}
							sx={{
								p: 1.5,
								borderRadius: 2,
								border: (theme) => `1px solid ${theme.palette.divider}`,
								height: '100%',
								minHeight: 0,
								display: 'flex',
								flexDirection: 'column',
								backgroundColor: COLORS.BACKGROUND_SECONDARY,
							}}
						>
							<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, flexShrink: 0 }}>
								{col.title}
							</Typography>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1.5,
									flex: 1,
									minHeight: 0,
									overflow: 'auto',
								}}
							>
								{tasksByStatus[col.status].map((task) => (
									<TaskCard key={task.id} task={task} />
								))}
							</Box>
						</Paper>
					))}
				</Box>
			) : (
				<Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Assignee</TableCell>
								<TableCell width={160}>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{SYNTHETIC_TASKS.map((task) => {
								const label =
									KANBAN_COLUMNS.find((c) => c.status === task.status)?.title ?? task.status;
								return (
									<TableRow key={task.id} hover>
										<TableCell sx={{ fontWeight: 600 }}>{task.title}</TableCell>
										<TableCell>{task.description}</TableCell>
										<TableCell>
											<Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
												<UserAvatar user={task.assignee} size="xs" />
											</Box>
										</TableCell>
										<TableCell>{label}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Paper>
			)}
		</Box>
	);
};
